import { createIntegration, MethodType, RedirectType, RedirectMethod } from '@endeavour/payment-integration';
import { NuveiMethodType, NuveiEnvironment } from './method.interface.js';
import { promisify } from './utils/promisify.js';
import { createPayment, CARD_METHOD } from './utils/create-payment.js';
import { loadScript } from './utils/load-script.js';
import { getForm } from './utils/get-form.js';
import { getSchemaFromFields } from './schema.js';

/// <reference path="./interface.d.ts" />
const SCRIPT_URL = 'https://cdn.safecharge.com/safecharge_resources/v1/websdk/safecharge.js';
const INTEGRATION_ID = 'nuvei';
let nuveiScript = null;
const createNuveiIntegration = (config) => {
    let safechargeApi = null;
    let safechargeSessionToken = null;
    let sessionData = null;
    let getSession = null;
    const getSessionData = async () => {
        if (!getSession) {
            throw new Error('[Nuvei] No session provider');
        }
        if (!sessionData) {
            sessionData = await getSession(INTEGRATION_ID);
        }
        return sessionData;
    };
    const getSafeChargeApi = async () => {
        const sessionData = await getSessionData();
        if (!nuveiScript) {
            nuveiScript = loadScript(SCRIPT_URL);
        }
        await nuveiScript;
        const contextId = sessionData.providerData.sessionContextExternalId;
        if (!safechargeApi || safechargeSessionToken !== contextId) {
            safechargeSessionToken = contextId;
            safechargeApi = window.SafeCharge({
                env: config.environment ?? NuveiEnvironment.INTEGRATION,
                merchantId: config.merchantId,
                merchantSiteId: config.merchantSiteId,
                sessionToken: safechargeSessionToken,
            });
        }
        return safechargeApi;
    };
    // Due to the way the SafeCharge Fields API works we have to store the instance of the fields in a provider global variable.
    // This is because the fields are created in the getCustomField method and then used in the processPayment method.
    // We cannot set the field value in getCustomField as most form APIs disallow non-primitive values being set in the form.
    // This sucks as we're now entirely stateful.
    let fieldsInstance = null;
    return createIntegration({
        name: INTEGRATION_ID,
        async initialize(getIntegrationSession) {
            getSession = getIntegrationSession;
        },
        async destroy() {
            fieldsInstance = null;
            safechargeApi = null;
            sessionData = null;
            safechargeSessionToken = null;
            getSession = null;
        },
        async getMethods({ methods, type }) {
            const sfc = await getSafeChargeApi();
            const apms = await promisify(sfc.getApms.bind(sfc))({
                type: type === MethodType.DEPOSIT
                    ? NuveiMethodType.DEPOSIT
                    : NuveiMethodType.WITHDRAWAL,
            });
            const methodMap = apms.paymentMethods.reduce((acc, method) => {
                acc[method.paymentMethod] = method;
                return acc;
            }, {});
            return methods
                .filter((method) => {
                return !!method && method.integrationId === INTEGRATION_ID;
            })
                .map((method) => {
                const nuveiMethod = methodMap[method.providerId];
                if (!nuveiMethod) {
                    return method;
                }
                return {
                    ...method,
                    image: method.image ?? nuveiMethod.logoURL,
                };
            });
        },
        async getSavedAccounts({ methods, type }) {
            const sfc = await getSafeChargeApi();
            const userPaymentMethods = await promisify(sfc.getUserUPOs.bind(sfc))();
            return userPaymentMethods.paymentMethods
                .map((upm) => {
                const method = methods.find((method) => method.providerId === upm.paymentMethodName);
                const successType = type === MethodType.DEPOSIT
                    ? 'depositSuccess'
                    : 'withdrawSuccess';
                if (!method ||
                    !method.isRepeatTransactionAvailable ||
                    upm[successType] !== 'true') {
                    return null;
                }
                return {
                    method,
                    name: upm.upoName,
                    accountId: upm.userPaymentOptionId?.toString() ?? '',
                };
            })
                .filter((account) => !!account);
        },
        async getMethodDetailsForm({ providerId, accountId, type }) {
            const sfc = await getSafeChargeApi();
            const allPaymentMethods = await promisify(sfc.getApms.bind(sfc))({
                type: type === MethodType.DEPOSIT
                    ? NuveiMethodType.DEPOSIT
                    : NuveiMethodType.WITHDRAWAL,
            });
            fieldsInstance = null;
            const method = allPaymentMethods.paymentMethods.find((method) => method.paymentMethod === providerId);
            if (type === MethodType.WITHDRAWAL) {
                return false;
            }
            const fields = getForm(method, !!accountId, providerId);
            return {
                fields,
                schema: getSchemaFromFields(fields),
            };
        },
        async processPayment({ providerId, accountId, type, formData }) {
            const preLaunchSessionData = await getSessionData();
            const launchResponse = await config.processPayment({
                externalPaymentMethodId: providerId,
                type: type === MethodType.DEPOSIT ? 'Deposit' : 'Withdrawal',
                sessionContextId: preLaunchSessionData.sessionId,
            });
            if (type === MethodType.WITHDRAWAL && launchResponse.withdrawalLaunchUrl) {
                return {
                    success: true,
                    redirectOutput: {
                        container: RedirectType.REDIRECT,
                        method: RedirectMethod.GET,
                        parameters: {},
                        url: launchResponse.withdrawalLaunchUrl,
                        width: window.innerWidth,
                        height: window.innerHeight,
                    },
                    transactionId: launchResponse.transactionId ?? preLaunchSessionData.sessionId,
                };
            }
            const requestData = createPayment(providerId, accountId, formData, fieldsInstance);
            const contextId = launchResponse.sessionContextExternalId;
            if (!contextId || !sessionData || !launchResponse.sessionContextId) {
                throw new Error('No launch session data');
            }
            // Set the new session data information
            sessionData.providerData.sessionContextExternalId = contextId;
            sessionData.sessionId = launchResponse.sessionContextId;
            const sfc = await getSafeChargeApi();
            await promisify(sfc.setOpenAmount.bind(sfc))({
                sessionToken: contextId,
                amount: formData?.amount,
            });
            const response = await promisify(sfc.createPayment.bind(sfc))({
                ...requestData,
                clientUniqueId: sessionData?.sessionId,
                sessionToken: contextId,
                merchantId: config.merchantId,
                merchantSiteId: config.merchantSiteId,
                apmWindowType: 'redirect',
            });
            return {
                success: response.result !== 'ERROR' && response.result !== 'DECLINED',
                error: response.errCode.toString(),
                transactionId: launchResponse.transactionId ?? sessionData.sessionId,
            };
        },
        getCustomField({ name }) {
            const container = document.createElement('div');
            const fieldArray = name === CARD_METHOD ? ['card'] : ['ccCvc'];
            return {
                async render(element) {
                    const sfc = await getSafeChargeApi();
                    container.innerHTML = '';
                    const fields = sfc.fields({});
                    for (const field of fieldArray) {
                        const fieldElement = document.createElement('div');
                        container.appendChild(fieldElement);
                        const nuveiField = fields.create(field, {});
                        nuveiField.attach(fieldElement);
                        fieldsInstance = nuveiField;
                    }
                    element.appendChild(container);
                },
                destroy() {
                    container.parentNode?.removeChild(container);
                    container.innerHTML = '';
                },
            };
        },
        getPendingWithdrawals() { },
        cancelWithdrawal({ configuration, transactionId }) {
            return Promise.resolve(true);
        },
    });
};

export { INTEGRATION_ID, createNuveiIntegration };
